import {
  CloudGroupUser,
  Recipient,
  RecipientResource,
  TicketResource,
} from 'types';
import { FeatureFlags } from './FeatureFlags';

const stringSortAZ = (a: string, b: string): number => (a < b ? -1 : 1);

const primaryRoleSort = (a: string, b: string): number =>
  b === 'Primary' ? 1 : -1;

const RecipientUtils = {
  /** Takes a Customer Admin Service Contact object and returns a Recipient */
  fromResource: (
    recipient: RecipientResource,
    creatorId?: string,
  ): Recipient => {
    let recipientToReturn = {
      ...recipient,
      _isChecked: false,
      _isCreator: recipient.id !== '' && recipient.id === creatorId,
      _isPrimary: recipient.roles
        ? recipient.roles.some((role) => role.toLowerCase() === 'primary')
        : false,
      _toRemove: false,
      /*
       * Unverified recipients come back with email as the only populated field
       * if the recipient does not have an id display its email, if not return it's full name.
       */
      fullName: !recipient.id
        ? recipient.email
        : `${recipient.firstName} ${recipient.lastName}`.trim(),
    };
    if (FeatureFlags.isActive('federated-recipients')) {
      /**
       * If no firstname + lastname => cloud federated user
       */
      const isFederatedUser =
        !recipient.hasOwnProperty('firstName') &&
        !recipient.hasOwnProperty('lastName');
      if (isFederatedUser) {
        recipientToReturn = {
          ...recipientToReturn,
          fullName: recipient.email,
          id: recipient.email,
          email: '',
          username: '',
        };
      }
    }
    return recipientToReturn;
  },

  /**
   * Takes array of role strings and returns a single string of comma-separated
   * roles sorted with primary first followed by role listed alphabetically
   */
  getDisplayRoles: (roles: string[]): string =>
    (roles || [])
      .sort(stringSortAZ)
      .sort(primaryRoleSort)
      .join(', '),

  isRecipientWithId: (
    option: Recipient | CloudGroupUser,
  ): option is Recipient => {
    return (option as Recipient).id !== undefined;
  },
  /**
   * Takes a Recipient[] and returns an array of rpn ID strings or the email
   * if no id exists.
   */

  getIds: (recipients: (Recipient | CloudGroupUser)[]) =>
    recipients.map(
      (recipient: Recipient | CloudGroupUser) =>
        RecipientUtils.isRecipientWithId(recipient)
          ? (recipient as Recipient)._isCloudTicketReviewer
            ? recipient.email
            : recipient.id || recipient.email
          : recipient.email,
    ),

  matchRpnsToRecipient: (rpn: string, recipients: Recipient[]): Recipient => {
    return (
      recipients.find((recipient: Recipient) => recipient.id === rpn) ||
      ({
        fullName: 'No User Found for RPN: ' + rpn,
        id: rpn,
      } as Recipient)
    );
  },

  /**
   * Takes a regex expression and returns a Recipient[] filter function that
   * search on name, username, email, and roles
   */
  makeFilter: (filterRx: RegExp) => (item: Recipient) =>
    [item.fullName, item.username, item.email, ...(item.roles || [])].some(
      (name) => filterRx.test(name),
    ),

  /** Takes two Recipients and returns sort order based on _isPrimary flag */
  sortByNameAndRoles: (a: Recipient, b: Recipient): number =>
    a._isCreator ||
    (a._isPrimary && !b._isCreator) ||
    (!b._isPrimary && a.fullName < b.fullName)
      ? -1
      : 1,

  /**
   * Takes array of all contacts and a ticket resource and returns an array of
   * that ticket's transformed recipients
   */
  mapTicketRecipients: (
    contacts: Recipient[],
    ticket: TicketResource,
    isDedicated: boolean,
  ) =>
    (ticket.recipients || []).map((recipient) =>
      RecipientUtils.fromResource(recipient, ticket.createdBy.id),
    ),

  /**
   * Takes a Recipient[] and filters for unique recipients (id based)
   * then returning that array
   */
  unique: (
    recipients: Recipient[] | CloudGroupUser[],
  ): Recipient[] | CloudGroupUser[] => {
    let uniqueIds = new Set();

    return recipients.filter((recipient: Recipient | CloudGroupUser) => {
      if (RecipientUtils.isRecipientWithId(recipient)) {
        if (uniqueIds.has(recipient.id || recipient.email)) {
          return false;
        }
        uniqueIds.add(recipient.id || recipient.email);
        return true;
      } else {
        if (uniqueIds.has(recipient.email)) {
          return false;
        }
        uniqueIds.add(recipient.email);
        return true;
      }
    });
  },

  /**
   * Takes a Recipient[] and returns an array with items flagged for removal
   * filtered out
   */
  withoutToRemove: (recipients: Recipient[]) =>
    recipients.filter((recipient: Recipient) => !recipient._toRemove),

  /**
   * Takes a Recipient[] and returns an array with items not flagged as
   * checked filtered out
   */
  withChecked: (recipients: Recipient[]) =>
    recipients.filter((recipient: Recipient) => recipient._isChecked),

  /** Create hash of user fields for text search */
  searchHash: (user: Recipient) =>
    `${user.fullName} ${user.email}
  }`.toLowerCase(),

  /**
   * Takes list of recipient and logged in user's username
   * Returns the recipient data for the matching username with creator flag as true
   */

  getCreatorRecipient: (
    recipients: Recipient[],
    creatorUsername: string,
    domainId: string,
    federatedIdp: string,
    userId: string,
    contactId: string,
    email: string,
  ): Recipient[] => {
    let creator = recipients.find((recipient) => {
      const isCloudFederatedUser =
        !recipient.hasOwnProperty('firstName') &&
        !recipient.hasOwnProperty('lastName');
      const isDedicatedFederatedUser =
        domainId.includes('dedicated') && federatedIdp;
      let isCurrentRecipientCreator = false;
      if (isCloudFederatedUser) {
        isCurrentRecipientCreator =
          email.toLowerCase() === recipient.id.toLowerCase();
      } else if (isDedicatedFederatedUser) {
        isCurrentRecipientCreator =
          recipient.legacyNumber && contactId === recipient.legacyNumber
            ? true
            : false;
      } else {
        // Match by username first, fallback to email if username is empty
        if (recipient.username) {
          isCurrentRecipientCreator =
            recipient.username.toLowerCase() === creatorUsername.toLowerCase();
        }
        if (!isCurrentRecipientCreator && email && recipient.email) {
          isCurrentRecipientCreator =
            recipient.email.toLowerCase() === email.toLowerCase();
        }
      }
      return isCurrentRecipientCreator;
    });
    if (creator) {
      return [
        {
          ...creator,
          _isCreator: true,
          _isChecked: true,
        },
      ];
    } else {
      return [];
    }
  },

  /**
   * Takes list of recipients
   * Returns the recipient data for the primary recipient if he's not the creator
   */

  getPrimaryRecipient: (recipients: Recipient[]): Recipient[] => {
    let primary = recipients.find(
      ({ roles, username }) =>
        roles ? roles.some((role) => role.toLowerCase() === 'primary') : false,
    );
    if (primary) {
      return [
        {
          ...primary,
          _isPrimary: true,
          _isChecked: true,
        },
      ];
    } else {
      return [];
    }
  },

  /**
   * Takes list of recipient and logged in user's username
   * Returns the recipient data with
   * isCreator and _isChecked flag as true for the matching username
   */
  setCreatorFlag: (recipients: Recipient[], creatorUsername: string) => {
    return recipients.map((recipient) => {
      const isCloudFederatedUser =
        !recipient.hasOwnProperty('firstName') &&
        !recipient.hasOwnProperty('lastName');
      if (
        !isCloudFederatedUser &&
        recipient.username.toLowerCase() === creatorUsername.toLowerCase()
      ) {
        return {
          ...recipient,
          _isCreator: true,
          _isChecked: true,
        };
      } else if (
        isCloudFederatedUser &&
        recipient.id.toLowerCase() === window.PORTAL_DATA.email
      ) {
        return {
          ...recipient,
          _isCreator: true,
          _isChecked: true,
        };
      } else {
        return recipient;
      }
    });
  },
};

export { RecipientUtils };
